import Box from '@mui/material/Box'
import Button from '@mui/material/Button'

export interface PillSegmentOption<T extends string> {
  value: T
  label: string
}

interface Props<T extends string> {
  value: T
  onChange: (value: T) => void
  options: readonly PillSegmentOption<T>[]
  testId?: string
  /** Metric cards — themed track + accent active label */
  variant?: 'metric' | 'chart'
  accentColor?: string
  trackBg?: string
  /** Optional fixed segment widths (px) — Figma 37:1431 SP/Sensemakers toggle */
  segmentWidths?: number[]
  /** Outer track width in px — defaults to 146 (Figma week/month) */
  width?: number
}

const CHART_ACCENT = '#008cd0'

export function PillSegmentToggle<T extends string>({
  value,
  onChange,
  options,
  testId,
  variant = 'chart',
  accentColor = CHART_ACCENT,
  trackBg = '#f0faf3',
  width = 146,
  segmentWidths,
}: Props<T>) {
  const isMetric = variant === 'metric'

  return (
    <Box
      data-testid={testId}
      sx={{
        position: 'relative',
        width,
        height: 31,
        borderRadius: '100px',
        bgcolor: isMetric ? trackBg : '#fff',
        border: isMetric ? 'none' : '1px solid #e5e7eb',
        p: '2px',
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      {options.map((option, index) => {
        const active = value === option.value
        const segmentWidth = segmentWidths?.[index]
        return (
          <Button
            key={option.value}
            onClick={() => onChange(option.value)}
            disableRipple
            sx={{
              flex: segmentWidth === undefined ? 1 : '0 0 auto',
              width: segmentWidth,
              minWidth: segmentWidth ?? 0,
              height: 27,
              px: 0,
              py: 0,
              borderRadius: '100px',
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '20px',
              color: active ? (isMetric ? accentColor : CHART_ACCENT) : '#a0abbf',
              bgcolor: active ? '#f9fafb' : 'transparent',
              border: active ? '1px solid #f1f1f1' : '1px solid transparent',
              boxShadow: active ? '0 4px 20px rgba(0, 0, 0, 0.12)' : 'none',
              filter: !active ? 'drop-shadow(0 4px 10px rgba(0, 0, 0, 0.12))' : 'none',
              '&:hover': {
                bgcolor: active ? '#f9fafb' : 'transparent',
              },
            }}
          >
            {option.label}
          </Button>
        )
      })}
    </Box>
  )
}
